import { useEffect, useState } from "react";

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  url: string;
}

export default function ShareModal({ open, onClose, title, url }: ShareModalProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    setCopied(false);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/60 px-4 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-background-50 p-6 animate-scale-in"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-bold text-foreground-950">分享影片</h3>
            <p className="mt-1 text-sm text-foreground-500">{title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭"
            className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 hover:bg-background-200"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="mt-5">
          <label className="text-xs font-medium text-foreground-600">分享链接</label>
          <div className="mt-2 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 p-1.5">
            <input
              readOnly
              value={url}
              className="w-full bg-transparent px-2 text-sm text-foreground-700 focus:outline-none"
            />
            <button
              type="button"
              onClick={handleCopy}
              className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-3 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className={copied ? "ri-check-line" : "ri-file-copy-line"}></i>
              {copied ? "已复制" : "复制"}
            </button>
          </div>
          {copied && <p className="mt-2 text-xs text-primary-600">链接已复制到剪贴板</p>}
        </div>

        <div className="mt-5 flex items-center gap-3">
          {[
            { icon: "ri-weibo-fill", label: "微博" },
            { icon: "ri-wechat-fill", label: "微信" },
            { icon: "ri-qq-fill", label: "QQ" },
            { icon: "ri-link", label: "链接" },
          ].map((item) => (
            <button
              key={item.label}
              type="button"
              onClick={handleCopy}
              className="flex flex-1 flex-col items-center gap-1.5 rounded-md border border-background-200 py-3 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className={`${item.icon} text-xl`}></i>
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}